import styled, { css } from 'styled-components'
import { SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from '../../styles/theme'
import { CATEGORY_STYLES } from '../../constants/mindmapConfig'

// Main page container — owns the scroll context so sticky header works inside it
export const ReportContainer = styled.div`
  height: calc(100dvh - 64px);
  overflow-y: auto;
  padding: 0 ${SPACING.xl} ${SPACING.xl};
  max-width: 1200px;
  margin: 0 auto;

  @media print {
    height: auto;
    overflow: visible;
    padding: ${SPACING.xl};
  }
`

// Header section
export const ReportHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${SPACING.md} 0;
  margin-bottom: ${SPACING.lg};
  background: var(--color-bg-primary);
  box-shadow: 0 1px 0 var(--color-border, #e8e8e8);

  @media print {
    position: static;
    box-shadow: none;
    padding: 0;
  }
`

// Idea container — connectors only appear when nested inside another idea (not at first level)
export const IdeaContainer = styled.div<{ $nested?: boolean }>`
  position: relative;

  ${({ $nested }) =>
    !$nested &&
    css`
      padding-left: 14px;

      /* Top-level bullet: marks each first-level idea so siblings read as distinct items */
      &::before {
        content: '';
        position: absolute;
        left: 2px;
        top: 17px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #ac6dff;
      }
    `}

  ${({ $nested }) =>
    $nested &&
    css`
      /* Vertical spine: runs full height to connect all siblings */
      &::before {
        content: '';
        position: absolute;
        left: -18px;
        top: 0;
        bottom: 0;
        width: 1.5px;
        background: #ac6dff;
      }

      /* Last sibling: spine stops exactly at the arrow, not below */
      &:last-child::before {
        bottom: auto;
        height: 19px;
      }

      /* Arrowhead — box-sizing: content-box ensures the border triangle renders correctly */
      &::after {
        content: '';
        position: absolute;
        box-sizing: content-box;
        left: -6px;
        top: 15px;
        width: 0;
        height: 0;
        border-left: 5px solid #ac6dff;
        border-top: 4px solid transparent;
        border-bottom: 4px solid transparent;
      }

      /* Horizontal connector on the direct IdeaRow child */
      & > .idea-row {
        position: relative;
      }

      & > .idea-row::before {
        content: '';
        position: absolute;
        left: -18px;
        top: 19px;
        width: 12px;
        height: 1px;
        background: #ac6dff;
      }
    `}
`

// Idea text content
export const IdeaTextContent = styled.div`
  flex: 1;
  padding: 0.25rem 0;
`

// Node labels container
export const NodeLabelsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
`

// Stakeholder feedback box
export const StakeholderFeedbackBox = styled.div`
  margin-top: ${SPACING.md};
  margin-bottom: ${SPACING.sm};
  margin-left: 0;
  padding: ${SPACING.md};
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.lg};
`

// Feedback header
export const FeedbackHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: ${SPACING.md};
`

// Feedback header text
export const FeedbackHeaderText = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

// Stakeholder card
export const StakeholderCard = styled.div`
  margin-top: ${SPACING.sm};
  padding: ${SPACING.sm};
  background-color: var(--color-bg-card);
  border-radius: ${BORDER_RADIUS.sm};
  border: 1px solid var(--color-border);
`

// Stakeholder header
export const StakeholderHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${SPACING.sm};
`

// Stakeholder name/role
export const StakeholderName = styled.span`
  font-weight: ${FONT_WEIGHT.semibold};
  font-size: ${TYPOGRAPHY.caption.fontSize};
`

export const StakeholderRole = styled.span`
  color: var(--color-text-secondary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-style: italic;
`

// Stakeholder rating text
export const StakeholderRatingText = styled.span`
  font-weight: ${FONT_WEIGHT.semibold};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-primary);
`

// Feedback fields container
export const FeedbackFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

// Feedback field
export const FeedbackField = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
`

// Feedback field label
export const FeedbackFieldLabel = styled.div`
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  margin-bottom: 3px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

// Feedback field content
export const FeedbackFieldContent = styled.div`
  color: var(--color-text-secondary);
  padding-left: 6px;
  border-left: 2px solid var(--color-border);
  line-height: 1.4;
`

// ============================================================================
// EDITORIAL DESIGN SYSTEM — Report Redesign
// ============================================================================

// Type badge — small colored ALL-CAPS label with icon
export const SectionTypeBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${(props) => props.$color};
  margin-bottom: ${SPACING.sm};
`

// Company block — transparent bg, colored border only
export const CompanyBlock = styled.div.attrs({ className: 'report-company-block' })<{
  $clickable?: boolean
  $selected?: boolean
}>`
  background: transparent;
  border: none;
  border-top: 3px solid ${CATEGORY_STYLES.venture.iconColor};
  border-radius: 0;
  padding: 0;
  padding-top: ${SPACING.md};
  margin-bottom: ${SPACING.lg};
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition: box-shadow 0.15s ease;
  ${(props) => props.$selected && 'box-shadow: 0 0 0 2px var(--color-primary, #6e53f9);'}

  &:hover {
    ${(props) =>
      props.$clickable && !props.$selected && 'box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);'}
  }
`

export const CompanyName = styled.h2`
  font-size: ${TYPOGRAPHY.h4.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  color: var(--color-text-primary);
  margin: 0 0 ${SPACING.xl};
  line-height: 1.2;
`

export const CompanyFieldLabel = styled.p`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--color-text-tertiary);
  margin: 0 0 ${SPACING.xs};
`

export const CompanyFieldText = styled.p`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  line-height: 1.6;
  color: var(--color-text-primary);
  margin: 0 0 ${SPACING.xl};
`

export const CompanyChildrenArea = styled.div``

// Unmet-need block — 2px left amber border
export const NeedBlock = styled.div.attrs({ className: 'need-block' })<{
  $clickable?: boolean
  $selected?: boolean
}>`
  border-left: 2px solid
    ${(props) =>
      props.$selected ? 'var(--color-primary, #6e53f9)' : CATEGORY_STYLES.need.iconColor};
  border-radius: 0 ${BORDER_RADIUS.md} ${BORDER_RADIUS.md} 0;
  background: ${(props) =>
    props.$selected
      ? 'color-mix(in srgb, var(--color-primary, #6e53f9) 5%, var(--color-bg-card))'
      : 'var(--color-bg-card)'};
  padding: ${SPACING.md} ${SPACING.lg};
  margin-bottom: ${SPACING.lg};
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition:
    background 0.15s ease,
    border-left-color 0.15s ease;

  &:hover:not(:has(.idea-row:hover)) {
    ${(props) => props.$clickable && 'background: var(--color-bg-hover);'}
  }
`

export const NeedStatement = styled.p`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  line-height: 1.6;
  color: var(--color-text-primary);
  margin: 0;
`

export const IdeasTreeLabel = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #ac6dff;
  margin: ${SPACING.xl} 0 ${SPACING.xs};
`

// Idea row — the clickable row for each idea item
export const IdeaRow = styled.div.attrs({ className: 'idea-row' })<{
  $clickable?: boolean
  $selected?: boolean
}>`
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.sm};
  padding: 4px ${SPACING.xs};
  border-radius: ${BORDER_RADIUS.sm};
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition: background 0.15s ease;
  ${(props) =>
    props.$selected &&
    'background: color-mix(in srgb, var(--color-primary, #6e53f9) 8%, transparent); box-shadow: inset 2px 0 0 var(--color-primary, #6e53f9);'}

  &:hover {
    ${(props) => props.$clickable && 'background: var(--color-bg-hover);'}
  }
`

// Ideas tree — only indented when nested inside another idea (not at first level)
export const IdeaChildrenArea = styled.div<{ $nested?: boolean }>`
  ${({ $nested }) =>
    $nested &&
    css`
      margin-left: 6px;
      padding-left: 18px;
    `}
`

// Standalone ideas section
export const StandaloneIdeasBlock = styled.div`
  border-top: 3px solid #ac6dff;
  padding-top: ${SPACING.xl};
  margin-top: ${SPACING.xl};
`

export const StandaloneIdeasTitle = styled.h3`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  margin: 0 0 ${SPACING.md};
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
`
